// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · 화면 전체가 공유하는 상수
//
//  ★ 여기 있는 값은 **전부 다른 곳에도 같은 값이 있다.** 한쪽만 고치면 조용히 틀린다.
//    그래서 값마다 「짝이 어디 있는지」를 적어 둔다.
// ─────────────────────────────────────────────────────────────────────────

/**
 * 로그인 코드 자릿수.
 *
 * ★ **하드코딩된 6 이 아니다.** apps/api 는 8자리를 쓴다 —
 *   `apps/api/wrangler.toml` 의 `CV_CODE_DIGITS = "8"`,
 *   `apps/api/src/routes/auth.ts` 의 `codeDigits()` 기본값 8.
 *
 * ⚠️ SCREEN-DESIGN.html 과 DB-DESIGN.md §6-1 에는 「6자리」로 적혀 있다. **문서가 낡았다.**
 *   6자리는 100만 가지라, 이메일당 분당 1회 제한을 그대로 두고 주소를 바꿔 가며
 *   하루 7,200번 시도하면 30일 누적 성공 확률이 **20%** 다. 8자리면 0.2% 다
 *   (`apps/api/README.md` §4-5). 그래서 **API 쪽 값이 맞다.**
 *
 * ⚠️ 자릿수가 바뀌면 여기와 wrangler.toml **둘 다** 고쳐야 한다. 한쪽만 고치면
 *   PinInput 칸 수가 안 맞아 사용자가 코드를 다 넣을 수 없거나, 다 넣어도 전송이 안 된다.
 *   그때 화면에는 아무 오류도 안 뜬다 — onComplete 가 영영 안 불릴 뿐이다.
 *
 * API 는 `/^\d{4,12}$/` 를 받으므로(auth.ts:189) 이 값이 그 범위 밖이면 무조건 401 이다.
 */
export const LOGIN_CODE_DIGITS = 8;

/**
 * 「코드 다시 보내기」 잠금 시간(초).
 *
 * ★ API 는 같은 이메일에 **분당 1회**만 코드를 보낸다(DB-DESIGN §6-1 규칙 2).
 *   그런데 제한에 걸려도 응답은 **204** 다 — 429 를 주면 그 자체가 "이 주소는 최근에
 *   요청했다 = 가입돼 있다" 는 신호가 되기 때문이다(apps/api/README §3).
 *
 * ⚠️ 즉 **화면이 세지 않으면 사용자는 아무 반응 없는 버튼을 계속 누른다.**
 *   서버는 매번 204 를 주고 메일은 안 가고 오류도 안 뜬다. 카운트다운은 장식이 아니라
 *   이 침묵을 사람에게 번역해 주는 유일한 장치다.
 */
export const RESEND_COOLDOWN_SEC = 60;

/**
 * 코드 유효 시간(분). DB-DESIGN §6-1 규칙 5.
 * 화면에서는 안내 문구에만 쓴다 — 만료 판정은 서버가 한다(만료도 401 이다).
 */
export const CODE_TTL_MIN = 10;

/**
 * 막혔을 때 연락할 곳.
 *
 * ★ **로그인 화면에 고정으로 노출한다.** 로그인 수단이 이메일 코드 하나뿐이라,
 *   스팸함·회사 메일 게이트웨이 격리·주소 오타로 막힌 사람은 **연락할 방법조차 없다.**
 *   "코드가 안 와요" 를 말할 곳이 없으면 그 사람은 그냥 시스템을 안 쓴다.
 *
 * ⚠️ 비밀값이 아니다. 담당자가 바뀌면 여기 한 줄만 고친다.
 */
export const CONTACT_EMAIL = 'basis@basis.co.kr';

/**
 * 가짜 응답 모드. **명시적으로 켤 때만** 동작한다.
 *
 *     VITE_MOCK=1 npm run dev        (PowerShell: $env:VITE_MOCK="1"; npm run dev)
 *
 * ★ API 없이 화면 배치를 만질 때만 쓴다. 켜져 있으면 화면 맨 위에 빨간 띠가 뜬다 —
 *   가짜 데이터를 진짜로 착각한 채 "잘 되네" 하는 것이 제일 위험하다.
 * ⚠️ 이걸 기본값으로 만들지 마라. 연결 실패 화면을 못 보게 되고, 그러면 그 화면이 썩는다.
 */
export const USE_MOCK = import.meta.env.VITE_MOCK === '1';

/*
 * ⚰️ [지운 것 2026-08-11 · 4바퀴] `export const PHONE_MAX_WIDTH_PX = 720`
 *
 *   ★ 왜 지웠나: **부르는 곳이 0건**이었다. 남아 있던 참조 넷은 전부 주석이다.
 *     그리고 붙어 있던 한 줄이 *"분기는 이 값 하나에만 있다(useIsPhone.ts)"* 라고
 *     **거짓말을 하고 있었다** — 실제 분기는 그때도 둘이었다:
 *         `breakpoint.ts` 의 `PHONE_MAX_PX = 767`   (앱 화면)
 *         `viewer/config.ts` 의 `PHONE_MAX_WIDTH`   (뷰어 — `viewer/react/useIsPhone.ts` 가 쓴다)
 *     죽은 값 자체보다 **그 한 줄이 더 위험했다.** 그 말을 믿고 새 화면에서 이걸
 *     import 하면, 721~767px 에서 앱과 그 화면이 서로 다른 판정을 하게 된다.
 *   ★ 지우는 것을 `breakpoint.ts` 가 *"config.ts 를 소유한 쪽에 맡긴다"* 로 이미
 *     넘겨 놨다(그 파일의 `PHONE_MAX_PX` 머리말). 그 숙제를 여기서 끝냈다.
 *
 *   ⚠️ **남은 뒤처리 한 줄** — 이 저장소의 `breakpoint.ts` 에 *"config.ts 의
 *      `PHONE_MAX_WIDTH_PX`(720)는 더 이상 쓰지 않는다 … 지우는 것은 config.ts 를
 *      소유한 쪽에 맡긴다"* 문단이 아직 남아 있다. 이제 **지웠으므로** 그 문단은
 *      「2026-08-11 에 지웠다」로 고쳐야 한다. 4바퀴의 뷰어 담당은 그 파일을 손댈
 *      구역이 아니라 못 고쳤다(앱 공통 구역의 한 줄).
 *      ★ `breakpoint.ts:9`·`:121` 과 `theme.ts:260` 의 「분기점이 넷이었다」 표는
 *        **역사 기록이라 그대로 맞다.** 거기서 이 이름을 지우지 마라 — 그 표의 값어치는
 *        「이런 일이 있었다」이지 「지금 그 값이 있다」가 아니다.
 *
 *   ⚠️ 폰 판정이 필요하면 `breakpoint.ts` 의 `useIsPhone()` 을, 뷰어 안이면
 *      `viewer/react/useIsPhone.ts` 를 써라. **새 숫자를 여기 다시 만들지 마라.**
 */
